import React, { useContext } from 'react';
import { omit } from 'lodash';
import { INTEGRATION_CONFIG_KEYS } from '@onward-delivery/core';
import { Button, Dialog, DialogContent, DialogTitle, Switch } from '@onward-delivery/design';
import IntegrationRow from './IntegrationRow';
import { INTEGRATION_TYPE_LABELS } from './constants';
import { CorpAdminContext } from '../store';

const SectionTitle = ({ children }) => (
    <span className="text-[11px] font-bold uppercase text-slate-500">{children}</span>
);

const EmptyNotice = ({ children }) => <span className="text-[13px] text-slate-500">{children}</span>;

// The shipper's own row replaces the default as soon as it exists, so nothing is written until the draft is saved.
const DefaultForShipperRow = ({ defaultIntegration, shipperId, draftKey }) => {
    const { callbacks } = useContext(CorpAdminContext);
    const { setCustomizeDrafts } = callbacks;

    const isManualDefault = defaultIntegration.type === 'MANUAL';
    const typeLabel = INTEGRATION_TYPE_LABELS[defaultIntegration.type] || defaultIntegration.type;
    const notice = isManualDefault ? 'The default has no TMS yet.' : `Using the default: ${typeLabel}`;

    const customize = () => {
        const unsharedKeys = isManualDefault ? [] : INTEGRATION_CONFIG_KEYS[defaultIntegration.type]?.unshared;
        const sharedConfig = omit(defaultIntegration.config, unsharedKeys);
        const draft = {
            carrier_id: defaultIntegration.carrier_id,
            middle_mile_carrier_id: defaultIntegration.middle_mile_carrier_id,
            client_id: defaultIntegration.client_id,
            shipper_id: shipperId,
            integration_meta_type_id: defaultIntegration.integration_meta_type_id,
            type: defaultIntegration.type,
            config: sharedConfig,
            default_order: defaultIntegration.default_order,
        };

        setCustomizeDrafts((drafts) => ({ ...drafts, [draftKey]: draft }));
    };

    const replace = () => {
        const draft = {
            carrier_id: defaultIntegration.carrier_id,
            middle_mile_carrier_id: defaultIntegration.middle_mile_carrier_id,
            client_id: defaultIntegration.client_id,
            shipper_id: shipperId,
            type: 'MANUAL',
        };

        setCustomizeDrafts((drafts) => ({ ...drafts, [draftKey]: draft }));
    };

    return (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <span className="text-[13px] text-slate-700">{notice}</span>
            <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={replace}>
                    Replace for this shipper
                </Button>
                <Button size="sm" onClick={customize}>
                    Customize for this shipper
                </Button>
            </div>
        </div>
    );
};

export const setupDraftKeyFor = (shipperId, carrierId) => `setup:${shipperId}:${carrierId}`;

/**
 * A blank MANUAL integration draft for one shipper and carrier, before anything is saved.
 * @param {string} shipperId
 * @param {string} carrierId
 */
export const setupDraftFor = (shipperId, carrierId) => ({
    carrier_id: carrierId,
    client_id: carrierId,
    shipper_id: shipperId,
    type: 'MANUAL',
});

/**
 * True when the shipper has no integration of its own and the carrier has no default for it to ride.
 * @param {Object[]} integrations
 * @param {Object[]} defaultIntegrations
 */
export const needsSetupDraft = (integrations, defaultIntegrations) =>
    !integrations.length && !defaultIntegrations.length;

export const defaultDraftKeyFor = (carrierId) => `default:${carrierId}`;

const CustomizeDraftRow = ({ draft, draftKey, ridesDefault }) => {
    const { callbacks } = useContext(CorpAdminContext);
    const { setCustomizeDrafts } = callbacks;

    const clearDraft = () => setCustomizeDrafts((drafts) => omit(drafts, draftKey));
    const adoptWrittenRow = (overrideId) =>
        setCustomizeDrafts((drafts) => ({ ...drafts, [draftKey]: { ...drafts[draftKey], override_id: overrideId } }));

    const notice = ridesDefault
        ? 'Not saved yet. This shipper keeps using the default until you save.'
        : 'Not saved yet.';

    return (
        <div className="flex flex-col gap-2">
            <span className="text-[13px] font-medium text-amber-700">{notice}</span>
            <IntegrationRow
                integration={draft}
                onSaved={clearDraft}
                onDiscard={clearDraft}
                onWritten={adoptWrittenRow}
            />
        </div>
    );
};

// Disabled on purpose: toggling capacity sync is not built yet, so the switch only reports it.
const CapacitySync = ({ hasCapacity }) => (
    <div className="flex items-center gap-3">
        <Switch checked={hasCapacity} disabled aria-label="Resource / Capacity" />
        <span className="text-[13px] text-slate-700">
            <span className="font-semibold text-slate-900">Resource / Capacity</span>{' '}
            {hasCapacity ? 'synced from DispatchTrack' : 'not synced from this integration'}
        </span>
    </div>
);

const IntegrationsModal = ({
    open,
    onClose,
    integrations = [],
    defaultIntegrations = [],
    shipperId,
    carrierId,
    shipperName,
    carrierName,
    hasCapacity = false,
}) => {
    const { state } = useContext(CorpAdminContext);
    const { customizeDrafts } = state;
    const isDefaultOnly = !shipperName;
    const titleName = carrierName ? `${shipperName} · ${carrierName}` : shipperName;
    const title = isDefaultOnly ? 'Default Integration' : `Integrations — ${titleName}`;

    const defaultRows = defaultIntegrations.map((integration) => (
        <IntegrationRow key={integration.override_id} integration={integration} />
    ));
    const shipperRows = integrations.map((integration) => (
        <IntegrationRow key={integration.override_id} integration={integration} />
    ));
    // Must agree with onward-functions' orgDefaultFor: only a row on the default's own carrier_id replaces it.
    const defaultForShipperRows = defaultIntegrations.reduce((rows, defaultIntegration) => {
        const replacedByOwnRow = integrations.some(
            (integration) => integration.carrier_id === defaultIntegration.carrier_id
        );
        if (replacedByOwnRow) return rows;

        const draftKey = `${shipperId}:${defaultIntegration.override_id}`;
        const draft = customizeDrafts[draftKey];
        if (draft) {
            rows.push(<CustomizeDraftRow key={draftKey} draft={draft} draftKey={draftKey} ridesDefault />);
            return rows;
        }

        rows.push(
            <DefaultForShipperRow
                key={draftKey}
                defaultIntegration={defaultIntegration}
                shipperId={shipperId}
                draftKey={draftKey}
            />
        );

        return rows;
    }, []);

    const setupDraftKey = setupDraftKeyFor(shipperId, carrierId);
    const setupDraft = needsSetupDraft(integrations, defaultIntegrations) ? customizeDrafts[setupDraftKey] : null;
    const setupDraftRow = setupDraft ? (
        <CustomizeDraftRow key={setupDraftKey} draft={setupDraft} draftKey={setupDraftKey} />
    ) : null;

    // Only the carrier's default window shows the default's own row, so its delete can't be mistaken for a shipper's.
    const defaultSection =
        isDefaultOnly && defaultIntegrations.length ? (
            <div className="flex flex-col gap-3">
                <SectionTitle>Default for All Shippers</SectionTitle>
                {defaultRows}
            </div>
        ) : null;

    const defaultDraftKey = defaultDraftKeyFor(carrierId);
    const defaultDraft = isDefaultOnly && !defaultIntegrations.length ? customizeDrafts[defaultDraftKey] : null;
    const defaultDraftRow = defaultDraft ? (
        <CustomizeDraftRow key={defaultDraftKey} draft={defaultDraft} draftKey={defaultDraftKey} />
    ) : null;

    const noDefaultNotice =
        isDefaultOnly && !defaultIntegrations.length && !defaultDraft ? (
            <EmptyNotice>No default integration yet.</EmptyNotice>
        ) : null;

    let shipperSection = null;
    if (!isDefaultOnly) {
        const shipperContent = [...shipperRows, ...defaultForShipperRows];
        if (setupDraftRow) shipperContent.push(setupDraftRow);

        shipperSection = shipperContent.length ? (
            <div className="flex flex-col gap-3">
                <SectionTitle>This Shipper</SectionTitle>
                {shipperContent}
            </div>
        ) : (
            <EmptyNotice>No integrations configured for this shipper.</EmptyNotice>
        );
    }

    const capacitySync = isDefaultOnly ? null : <CapacitySync hasCapacity={hasCapacity} />;

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) onClose();
            }}
        >
            <DialogContent
                aria-describedby={undefined}
                className="flex max-h-[90vh] w-[786px] max-w-[95vw] flex-col overflow-hidden rounded-2xl p-0"
            >
                <div className="border-b border-border px-6 pb-4 pt-5">
                    {/* mr-6 keeps clear of DialogContent's own close button. */}
                    <DialogTitle className="mr-6 text-xl font-bold text-foreground">{title}</DialogTitle>
                </div>
                <div className="flex flex-col gap-5 overflow-y-auto px-6 py-5">
                    {defaultSection}
                    {noDefaultNotice}
                    {defaultDraftRow}
                    {shipperSection}
                    {capacitySync}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default IntegrationsModal;
